import React from "react";
import { func } from "../../../../../utils";
import analytics from "../../../../../utils/analytics";

import {
  Chip,
  Container,
  Section,
  // ImageBadgeCity,
  LocationWrapper,
  TextCity,
  TextDate,
  TextLocation,
  TextWrapper,
  TextDefault,
  Button,
  SwooshContainer,
  DesktopContainer,
  DesktopHeader,
  DesktopHeaderLeft,
  DesktopBody,
  FooterBody,
  ImageBadgeCity,
  EmptySpace,
  PeriodRow,
  TextPeriodValue,
  ChipSlot,
} from "./CardItem.styled";
import useWindowSize from "../../../../../hooks/useWindowSize";
import { DataEventSeriesItemsProps } from "../../Timeline";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  useCategoryCompetition,
  useCitySchool,
  useCurrentTimelineSection,
} from "../../../../../store/user";
import useTimelineCardType from "./hooks";
import Show from "../../../FormRegistration/components/Show";
import { getSeriesCardState } from "../../../../../utils/seriesCardState";

const imgPath = `${process.env.REACT_APP_API_HOST}/uploads/featured-images/`;

export interface CardItemProps {
  data: DataEventSeriesItemsProps;
  /** When true, render the unified series lifecycle (Registrasi → … → Tonton
   *  Live) computed from event attributes, instead of the section-based states. */
  lifecycle?: boolean;
}

const CardItem: React.FC<CardItemProps> = ({ data, lifecycle = false }) => {
  const navigate = useNavigate();
  const currentDay = moment();
  const { size } = useWindowSize();
  //   @ts-ignore
  const isMobileView = (size?.width || 0) <= 768;

  const [isBadgeCityError, setIsBadgeCityError] =
    React.useState<boolean>(false);

  const { section } = useCurrentTimelineSection();
  const { setCategoryCompetition } = useCategoryCompetition();

  const { setCitySchool } = useCitySchool();

  const cardType = useTimelineCardType({
    eventStartDate: data?.event_start_date,
    eventEndDate: data?.event_end_date,
    registrationStartDate: data?.registration_start_date,
    registrationEndDate: data?.registration_end_date,
    danceRegistrationStartDate: data?.dance_registration_start_date,
    danceRegistrationEndDate: data?.dance_registration_end_date,
    videoUrl: data?.video_url,
    registrationUrl: data?.registration_url,
    isRegistrationOpen: data?.is_registration_open,
  });

  const withinFutsalRegistrationDate = moment(currentDay)?.isBetween(
    data?.registration_start_date,
    data?.registration_end_date,
    "day",
    "[]",
  );

  const withinDanceRegistrationDate = moment(currentDay)?.isBetween(
    data?.dance_registration_start_date,
    data?.dance_registration_end_date,
    "day",
    "[]",
  );

  const activeCategoryCompetition = withinFutsalRegistrationDate
    ? "Futsal"
    : "Dance";

  const isCardClickable =
    cardType === "event_day_with_video_url" && section === "watch";

  const lifecycleState = lifecycle ? getSeriesCardState(data) : null;

  const generateRemainingStartRegistration = (startDate: string) => {
    return moment(startDate)?.diff(currentDay, "day") + 1;
  };

  const generateDate = (
    startDate: string,
    endDate: string,
    withYears: boolean = false,
  ) => {
    if (!startDate || !endDate) return "Belum Tersedia";

    const mStartDate = moment(startDate);
    const mEndDate = moment(endDate);

    const isSameDay = mStartDate?.isSame(mEndDate, "day");
    const isSameMonth = mStartDate?.isSame(mEndDate, "month");
    const isSameYears = mStartDate?.isSame(mEndDate, "year");

    if (isSameDay) {
      return mStartDate?.format(withYears ? "DD MMMM YYYY" : "DD MMMM");
    }

    if (isSameMonth) {
      if (withYears) {
        return `${mStartDate?.format("DD")} - ${mEndDate?.format("DD MMMM YYYY")}`;
      }
      return `${mStartDate?.format("DD")} - ${mEndDate?.format("DD MMMM")}`;
    }

    if (withYears && isSameYears) {
      if (isSameYears) {
        return `${mStartDate?.format("DD MMMM")} - ${mEndDate?.format("DD MMMM YYYY")}`;
      }
    }

    if (withYears) {
      return `${mStartDate?.format("DD MMMM YYYY")} - ${mEndDate?.format("DD MMMM YYYY")}`;
    }

    return `${mStartDate?.format("DD MMMM")} - ${mEndDate?.format("DD MMMM")}`;
  };

  const remainingDays = generateRemainingStartRegistration(
    data?.event_start_date,
  );

  // "Daftarkan Sekolahmu" (registration section) focuses on the REGISTRATION
  // window only — not data.status, which tracks the event lifecycle and goes
  // stale in the CMS (e.g. status "upcoming" while registration is already open).
  const registrationStatus: "upcoming" | "open" | "past" = (() => {
    const start = data?.registration_start_date;
    const end = data?.registration_end_date;
    if (!start || !end) return "past";
    if (currentDay.isBefore(moment(start), "day")) return "upcoming";
    if (currentDay.isAfter(moment(end), "day")) return "past";
    return "open";
  })();

  const generateChip = () => {
    if (section === "registration") {
      switch (registrationStatus) {
        case "upcoming":
          return <Chip bgColor="rgba(31, 45, 61, 0.70)">Akan Datang</Chip>;
        case "past":
          return <Chip bgColor="rgba(31, 45, 61, 0.70)">Udah Lewat</Chip>;

        default:
          // Live event whose start date already passed → the countdown would go
          // negative ("-7 Hari Lagi"); show it as ongoing instead.
          return (
            <Chip bgColor="#0397C9" color="white">
              {remainingDays > 0 ? `${remainingDays} Hari Lagi` : "Berlangsung"}
            </Chip>
          );
      }
    }

    switch (cardType) {
      case "upcoming":
        return <Chip bgColor="rgba(31, 45, 61, 0.70)">Akan Datang</Chip>;
      case "before_event_in_registration_period":
      case "before_event_not_in_registration_period":
        return (
          <Chip bgColor="#0397C9" color="white">
            {remainingDays} Hari Lagi
          </Chip>
        );
      case "event_day_without_video_url":
        return <Chip bgColor="#0397C9">Berlangsung</Chip>;
      case "event_day_with_video_url":
        return <Chip bgColor="#FF110D">Live</Chip>;
      case "past_event":
        return <Chip bgColor="rgba(31, 45, 61, 0.70)">Udah Lewat</Chip>;

      default:
        break;
    }
  };

  const handleGoToSpecificCity = (event) => {
    event.stopPropagation();

    const theSlug = data?.slug === "soc-2025" ? "soc" : data?.slug;

    navigate(`/${theSlug}`, {
      state: {
        city: theSlug,
      },
    });
  };

  const handleRegister = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    analytics.sendEvent({
      event: "registration_2_pialabyu",
      title: data?.region_name,
    });
    setCategoryCompetition(activeCategoryCompetition);
    // @ts-ignore
    setCitySchool({
      ...data,
      city: data?.region_name,
      region_name: data?.region_name,
      cardDate: generateDate(
        data?.event_start_date,
        data?.event_end_date,
        true,
      ),
      event_place: data?.event_location,
      region_logo: isMobileView ? data?.key_visual_mobile : data?.key_visual,
      activeCategoryCompetition: activeCategoryCompetition,
    });
    navigate(`/${data?.slug}/registrasi`, {
      state: {
        city: data?.slug,
      },
    });
  };

  const generateButton = () => {
    if (section === "registration") {
      switch (registrationStatus) {
        case "upcoming":
          return (
            <Button
              style={{ cursor: "no-drop" }}
              disabled
              bgColor="transparent"
              color="#B8C1CB"
              borderColor="#B8C1CB"
            >
              Akan Datang
            </Button>
          );
        case "past":
          return (
            <Button
              style={{ cursor: "no-drop", fontSize: isMobileView ? 12 : "" }}
              disabled
              bgColor="transparent"
              color="#B8C1CB"
              borderColor="#B8C1CB"
            >
              Registrasi Ditutup
            </Button>
          );

        default:
          return <Button onClick={handleRegister}>Registrasi</Button>;
      }
    }

    switch (cardType) {
      /** Temporary until next phase */
      case "upcoming":
        return (
          <Button
            style={{ cursor: "no-drop" }}
            disabled
            bgColor="transparent"
            color="#B8C1CB"
            borderColor="#B8C1CB"
          >
            Akan Datang
          </Button>
        );
      case "past_event":
        return (
          <Button
            bgColor="transparent"
            color="#1F2D3D"
            borderColor="#1F2D3D"
            onClick={handleGoToSpecificCity}
          >
            Lihat Detail
          </Button>
        );
      case "event_day_without_video_url":
      case "before_event_not_in_registration_period":
      case "before_event_in_registration_period":
        return (
          <Button
            bgColor="white"
            color="#00BCF1"
            borderColor="#00BCF1"
            onClick={handleGoToSpecificCity}
          >
            Lihat Detail
          </Button>
        );
      case "event_day_with_video_url":
        return (
          <Button
            onClick={(event) => {
              event.stopPropagation();
              window.open(data?.video_url, "_blank");
            }}
            bgColor="#FF110D"
            color="white"
            borderColor="#FF110D"
          >
            Tonton Live
          </Button>
        );
      default:
        break;
    }
  };

  const itemData = {
    city: data?.region_name,
    eventDate: generateDate(data?.event_start_date, data?.event_end_date),
    registrationDate: generateDate(
      data?.registration_start_date,
      data?.registration_end_date,
    ),
    danceRegistrationDate: generateDate(
      data?.dance_registration_start_date,
      data?.dance_registration_end_date,
    ),
    remainingDays,
    location: data?.event_location,
    region_logo: isMobileView ? data?.key_visual_mobile : data?.key_visual,
  };

  // Badge kota pakai key visual dari API, dengan badge statis sebagai fallback
  // buat dua kasus: region yang ngirim string kosong, dan nama file yang ada di
  // response tapi belum keunggah di CMS (404 -> onError).
  const staticBadgeCity = func.getStaticImg(
    "illustration/piala-byU-2025-d.png",
  );
  const badgeCitySrc =
    !!itemData?.region_logo && !isBadgeCityError
      ? `${imgPath}${itemData?.region_logo}`
      : staticBadgeCity;

  // Unified lifecycle variant (HomePressCon's CitySeries): badge + CTA come from
  // event attributes (getSeriesCardState), rendered via CardItem's own Chip/Button.
  const renderLifecycleChip = () => (
    <Chip bgColor={lifecycleState?.badgeBgColor || "#0397C9"} color="white">
      {lifecycleState?.badge}
    </Chip>
  );

  const renderLifecycleButton = () => {
    const onClick =
      lifecycleState?.action === "register"
        ? handleRegister
        : lifecycleState?.action === "watch"
          ? (event: React.MouseEvent) => {
              event.stopPropagation();
              window.open(data?.video_url, "_blank");
            }
          : lifecycleState?.action === "detail"
            ? handleGoToSpecificCity
            : undefined;

    return (
      <Button
        disabled={lifecycleState?.buttonDisabled}
        style={
          lifecycleState?.buttonDisabled ? { cursor: "no-drop" } : undefined
        }
        bgColor={lifecycleState?.buttonBgColor}
        color={lifecycleState?.buttonTextColor}
        borderColor={lifecycleState?.buttonBorderColor}
        onClick={onClick}
      >
        {lifecycleState?.buttonText}
      </Button>
    );
  };

  const chip = lifecycle ? renderLifecycleChip() : generateChip();
  const button = lifecycle ? renderLifecycleButton() : generateButton();
  const badgeGrayScale = lifecycle
    ? !!lifecycleState?.grayScale
    : section === "registration"
      ? ["upcoming", "past"].includes(registrationStatus)
      : cardType === "upcoming";
  const cardClickable = lifecycle
    ? lifecycleState?.action !== "none"
    : isCardClickable;
  // Lifecycle keeps the registration layout (Pendaftaran Futsal row + full height).
  const showRegistrationPeriod = lifecycle || section === "registration";

  return (
    <React.Fragment>
      {isMobileView ? (
        <Container
          onClick={cardClickable ? handleGoToSpecificCity : undefined}
          isWatch={section === "watch"}
        >
          <SwooshContainer>
            <img
              src={func.getStaticImg(
                isMobileView
                  ? "/bg/swosh-abstrac-new.svg"
                  : "/bg/swosh-abstrac_d.svg",
              )}
              alt="swoosh"
            />
          </SwooshContainer>
          <Section>
            {chip}
            <ImageBadgeCity
              grayScale={badgeGrayScale}
              src={badgeCitySrc}
              onError={() => setIsBadgeCityError(true)}
            />

            <TextWrapper>
              <TextCity>{itemData?.city}</TextCity>
              <TextDate>{itemData?.eventDate}</TextDate>

              <LocationWrapper>
                <img
                  src={func.getStaticImg(`/illustration/pin location.svg`)}
                />
                <TextLocation>{itemData?.location}</TextLocation>
              </LocationWrapper>
            </TextWrapper>

            <Show when={showRegistrationPeriod}>
              <div>
                <TextWrapper>
                  <TextDefault>Pendaftaran Futsal</TextDefault>
                  <TextDefault className="!font-bold">
                    {itemData?.registrationDate}
                  </TextDefault>
                </TextWrapper>
              </div>
            </Show>

            {button}
          </Section>
        </Container>
      ) : (
        <DesktopContainer
          onClick={cardClickable ? handleGoToSpecificCity : undefined}
          isWatch={section === "watch"}
        >
          <SwooshContainer>
            <img
              src={func.getStaticImg(
                isMobileView
                  ? "/bg/swosh-abstrac-new.svg"
                  : "/bg/swosh-abstrac_d.svg",
              )}
              alt="swoosh"
            />
          </SwooshContainer>
          <Section>
            <ChipSlot isHidden={false}>{chip}</ChipSlot>
          </Section>
          <DesktopHeader>
            <DesktopHeaderLeft>
              <TextCity>{itemData?.city}</TextCity>
              <TextDate>{itemData?.eventDate}</TextDate>
              <LocationWrapper>
                <img
                  src={func.getStaticImg(`/illustration/pin location.svg`)}
                />
                <TextLocation>{itemData?.location}</TextLocation>
              </LocationWrapper>
            </DesktopHeaderLeft>
            <ImageBadgeCity
              grayScale={badgeGrayScale}
              src={badgeCitySrc}
              onError={() => setIsBadgeCityError(true)}
            />
          </DesktopHeader>
          <Show
            when={showRegistrationPeriod}
            fallbackComponent={<EmptySpace height="20px" />}
          >
            <DesktopBody>
              <PeriodRow>
                <TextDefault>Pendaftaran Futsal</TextDefault>
                <TextPeriodValue className="!font-bold !text-[12px] !text-[#6A7481]">
                  {itemData?.registrationDate}
                </TextPeriodValue>
              </PeriodRow>
            </DesktopBody>
          </Show>

          <FooterBody>{button}</FooterBody>
        </DesktopContainer>
      )}
    </React.Fragment>
  );
};

export default CardItem;
